import { storage } from './storage';

/**
 * Type pour l'état d'authentification dans le Router Context
 */
export interface RouterAuthContext {
  isAuthenticated: boolean;
  user: {
    email: string;
    firstName: string;
    lastName: string;
    company: string;
    role?: string;
    defaultOrganizationId?: string;
  } | null;
}

/**
 * Interface pour les tokens JWT
 */
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  accessTokenValidUntil: string;
  refreshTokenValidUntil: string;
}

/**
 * Fonction de notification pour les changements d'auth
 * Configurée par setAuthChangeNotifier() au démarrage de l'app
 */
let notifyAuthChange: (() => void) | null = null;

/**
 * Configure la fonction de notification des changements d'auth
 * Doit être appelé au démarrage de l'application (dans main.tsx)
 *
 * @param notifier - Fonction à appeler quand l'auth change
 */
export function setAuthChangeNotifier(notifier: () => void): void {
  notifyAuthChange = notifier;
}

/**
 * Charge l'état d'authentification depuis le storage
 */
export function loadAuthContext(): RouterAuthContext {
  const isAuthenticated = storage.getIsAuthenticated();

  if (!isAuthenticated) {
    return {
      isAuthenticated: false,
      user: null
    };
  }

  return {
    isAuthenticated: true,
    user: {
      email: storage.getUserEmail() || '',
      firstName: storage.getUserFirstName() || '',
      lastName: storage.getUserLastName() || '',
      company: storage.getUserCompany() || '',
      role: storage.getUserRole() || undefined,
      defaultOrganizationId: storage.getOrganizationId() || undefined
    }
  };
}

/**
 * Valide si la session est toujours valide
 */
export function isSessionValid(): boolean {
  const isAuthenticated = storage.getIsAuthenticated();
  const accessToken = storage.getAccessToken();
  // La session est valide si l'utilisateur est authentifié ET possède un token
  // Tant que le token est présent, on laisse l'API décider (401) au lieu d'expirer côté client
  return isAuthenticated && !!accessToken;
}

/**
 * Efface toutes les données d'authentification
 */
export function clearAuthContext(): void {
  storage.setIsAuthenticated(false);
  storage.setUserEmail('');
  storage.setUserFirstName('');
  storage.setUserLastName('');
  storage.setUserCompany('');
  storage.setUserRole('');
  storage.setProjectId('');
  storage.clearSessionExpiry();
  storage.setHasCompletedOnboarding(false);

  // Clear tokens
  storage.clearTokens();

  // Notifier le changement pour mettre à jour les composants React
  notifyAuthChange?.();
}

/**
 * Sauvegarde les informations d'authentification
 */
export function saveAuthContext(
  user: {
    email: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    role?: string;
    defaultOrganizationId?: string;
  },
  tokens: AuthTokens
): void {
  // Sauvegarder user data
  storage.setIsAuthenticated(true);
  storage.setUserEmail(user.email);
  if (user.firstName) storage.setUserFirstName(user.firstName);
  if (user.lastName) storage.setUserLastName(user.lastName);
  if (user.company) storage.setUserCompany(user.company);
  storage.setUserRole(user.role ?? '');

  // Sauvegarder les tokens
  storage.setAccessToken(tokens.accessToken);
  storage.setRefreshToken(tokens.refreshToken);
  storage.setAccessTokenExpiry(tokens.accessTokenValidUntil);
  storage.setRefreshTokenExpiry(tokens.refreshTokenValidUntil);

  // Set session expiry basé sur accessToken expiry
  const expiryTime = new Date(tokens.accessTokenValidUntil).getTime();
  storage.setSessionExpiry(expiryTime);

  // Sauvegarder organizationId si présent
  if (user.defaultOrganizationId) {
    storage.setOrganizationId(user.defaultOrganizationId);
  } else {
    storage.setOrganizationId('');
  }

  // Notifier le changement pour mettre à jour les composants React
  notifyAuthChange?.();
}

/**
 * Récupère le token d'accès
 */
export function getAccessToken(): string | null {
  return storage.getAccessToken();
}
